import moment from 'moment';
import './EventRenderer.css';
import { useTranslation } from 'react-i18next';

// Card layout mirrors AllClasses' renderEventContent so the teacher calendar
// and the admin calendar read the same: time row on top, title under it, a
// white platform/branch pill bottom-left and attendance badges bottom-right.
// The wrapper .fc-event is painted transparent in CSS, so every colour here
// comes from the event itself (set in Calendar.js).
const EventRenderer = ({ eventInfo }) => {
  const { t } = useTranslation();
  const event = eventInfo.event;
  const isClass = event.id.startsWith('class-');
  const isShared = event.extendedProps?.shared;

  const props = event.extendedProps || {};
  // Older shapes nested the class fields one level deeper.
  const nested = props.extendedProps || props;

  const startTime = event.start ? moment(event.start).format('HH:mm') : '';
  const endTime = event.end ? moment(event.end).format('HH:mm') : '';

  const borderColor = event.borderColor || '#999999';
  const backgroundColor = event.backgroundColor || 'transparent';
  const textColor = event.textColor || '#333333';

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'online':
        return '💻';
      case 'onsite':
        return '📍';
      case 'athome':
        return '🏠';
      default:
        return '❓';
    }
  };

  const renderAttendanceBadges = () => {
    if (!isClass) return null;
    const records = nested?.attendance_statuses || props?.attendance_statuses || [];
    if (records.length === 0) return null;

    const counts = records.reduce((acc, r) => {
      acc[r.status] = (acc[r.status] || 0) + 1;
      return acc;
    }, {});

    const badge = (label, count, bg, color, border) => count > 0 && (
      <span
        key={label}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 16,
          padding: '0 6px',
          marginLeft: 2,
          borderRadius: 8,
          background: bg,
          color,
          border: border ? `1px solid ${border}` : 'none',
          fontSize: 10,
          fontWeight: 700,
          lineHeight: 1,
          whiteSpace: 'nowrap',
        }}
      >
        {label}{count > 1 ? ` ${count}` : ''}
      </span>
    );

    const badges = [
      badge('Present', counts['Present'] || 0, '#52c41a', '#ffffff'),
      badge('Absent', counts['Absent'] || 0, '#ffffff', '#cf1322', '#ff4d4f'),
      badge('TBR', counts['To be rescheduled'] || 0, '#ff4d4f', '#ffffff'),
      badge('Late', counts['Late'] || 0, '#faad14', '#ffffff'),
    ].filter(Boolean);

    if (badges.length === 0) return null;

    return (
      <div
        style={{
          position: 'absolute',
          bottom: 2,
          right: 2,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          gap: 2,
          maxWidth: '85%',
          pointerEvents: 'none',
        }}
      >
        {badges}
      </div>
    );
  };

  const renderPlatformPill = () => {
    if (!isClass) return null;
    const platform = nested?.platform;
    const branchName = nested?.branchName || nested?.branch_name;
    const hasPlatform = platform && platform !== 'null' && platform !== '';
    const hasBranch = branchName && branchName !== 'null';
    if (!hasPlatform && !hasBranch) return null;

    return (
      <div
        style={{
          position: 'absolute',
          bottom: 2,
          left: 2,
          background: 'rgb(255, 255, 255)',
          padding: '2px 4px',
          borderRadius: 3,
          fontSize: '0.9em',
          color: '#333',
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          whiteSpace: 'nowrap',
        }}
      >
        {hasPlatform && `${getPlatformIcon(platform)} ${t(platform)}`}
        {hasPlatform && hasBranch && ' '}
        {hasBranch && `🏢 ${branchName}`}
      </div>
    );
  };

  // Classes carry a confirmed / not-confirmed marker; tasks have none.
  const confirmIcon = isClass ? (nested?.studentConfirm ? ' ✅' : ' ❌') : '';

  return (
    <div
      className={`event-content ${isClass ? 'event-class' : isShared ? 'event-shared' : 'event-task'}`}
      style={{
        // Keep drag handling with FullCalendar, not this markup.
        pointerEvents: 'none',
        position: 'relative',
        height: '100%',
        width: '100%',
        padding: 4,
        borderRadius: 4,
        minHeight: '100%',
        fontSize: '1.05em',
        border: `2px solid ${borderColor}`,
        background: backgroundColor,
        color: textColor,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          marginBottom: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexWrap: 'wrap',
          fontSize: '1.35em',
          fontWeight: 700,
          lineHeight: 1.2,
          color: textColor,
        }}
      >
        <span>{startTime} - {endTime}{confirmIcon}</span>
      </div>

      <div style={{ marginBottom: isClass ? 20 : 0, color: textColor }}>
        {event.title}
        {isShared && <span style={{ marginLeft: 4 }}>👥</span>}
      </div>

      {renderPlatformPill()}
      {renderAttendanceBadges()}
    </div>
  );
};

export default EventRenderer;
